<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const sections = [
  {
    title: '📦 Apa itu Bahan Baku?',
    icon: 'ri-archive-line',
    color: 'info',
    content: `
      <p class="mb-4">
        <strong>Bahan baku</strong> adalah semua material atau bahan yang Anda gunakan
        untuk membuat produk yang dijual. Setiap kali Anda menjual satu produk,
        sistem PintuKasir akan otomatis <strong>mengurangi stok bahan baku</strong>
        sesuai resep produk tersebut.
      </p>

      <h6 class="text-subtitle-1 font-weight-bold mb-3">Contoh untuk Coffee Shop:</h6>
      <VAlert type="info" variant="tonal" class="mb-4">
        <p class="mb-2">Untuk membuat <strong>1 cup Latte</strong>, Anda butuh:</p>
        <ul class="mb-0">
          <li>Kopi Arabika — 18 gram</li>
          <li>Susu Full Cream — 200 ml</li>
          <li>Gula Pasir — 15 gram</li>
          <li>Es Batu — 100 gram</li>
          <li>Cup Plastik 16oz — 1 pcs</li>
          <li>Sedotan — 1 pcs</li>
        </ul>
      </VAlert>

      <p class="mb-4">
        Setiap bahan di atas perlu didaftarkan satu per satu di menu <strong>Bahan Baku</strong>.
        Setelah itu, Anda bisa membuat <strong>resep produk</strong> yang menghubungkan
        produk dengan bahan-bahannya.
      </p>

      <h6 class="text-subtitle-1 font-weight-bold mb-3">Contoh untuk Usaha Lain:</h6>
      <VRow>
        <VCol cols="12" md="6">
          <VCard variant="outlined" class="mb-3">
            <VCardText>
              <div class="d-flex align-center mb-2">
                <VIcon icon="ri-bread-line" color="warning" class="me-2" />
                <strong>Bakery</strong>
              </div>
              <ul class="text-body-2 mb-0">
                <li>Tepung terigu (kg)</li>
                <li>Telur (butir)</li>
                <li>Mentega (gram)</li>
                <li>Gula pasir (gram)</li>
                <li>Kemasan kotak (pcs)</li>
              </ul>
            </VCardText>
          </VCard>
        </VCol>
        <VCol cols="12" md="6">
          <VCard variant="outlined" class="mb-3">
            <VCardText>
              <div class="d-flex align-center mb-2">
                <VIcon icon="ri-scissors-2-line" color="primary" class="me-2" />
                <strong>Barbershop</strong>
              </div>
              <ul class="text-body-2 mb-0">
                <li>Pomade (ml)</li>
                <li>Shampoo (ml)</li>
                <li>Razor blade (pcs)</li>
                <li>Handuk kertas (pcs)</li>
                <li>Tisu toilet (lembar)</li>
              </ul>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>

      <h6 class="text-subtitle-1 font-weight-bold mb-2">Mengapa Penting Mencatat Bahan Baku?</h6>
      <ul class="mb-0">
        <li class="mb-2">📉 <strong>Kontrol stok otomatis</strong> — sistem tahu kapan bahan hampir habis</li>
        <li class="mb-2">💰 <strong>Hitung HPP akurat</strong> — tahu berapa modal tiap produk</li>
        <li class="mb-2">🔔 <strong>Peringatan stok rendah</strong> — tidak kehabisan bahan saat ramai</li>
        <li class="mb-2">📊 <strong>Laporan penggunaan</strong> — tahu bahan apa yang paling banyak terpakai</li>
      </ul>
    `,
  },

  {
    title: '➕ Cara Menambah Bahan Baku Baru',
    icon: 'ri-add-circle-line',
    color: 'success',
    content: `
      <p class="mb-4">
        Ikuti langkah-langkah berikut untuk mendaftarkan bahan baku baru ke sistem PintuKasir.
      </p>

      <ol class="mb-4" style="line-height: 2.2">
        <li>
          Buka menu <strong>Inventory</strong> di sidebar kiri,
          lalu klik <strong>Bahan Baku</strong>
        </li>
        <li>
          Klik tombol <strong>"+ Tambah Bahan"</strong> di pojok kanan atas halaman
        </li>
        <li>
          Isi form yang muncul (penjelasan detail setiap kolom ada di bawah):
          <ul class="mt-2 mb-2">
            <li><strong>Nama Bahan Baku</strong> — wajib diisi</li>
            <li><strong>SKU</strong> — boleh dikosongkan</li>
            <li><strong>Satuan</strong> — wajib diisi</li>
            <li><strong>Kategori</strong> — boleh dikosongkan</li>
            <li><strong>Stok Minimum</strong> — sangat disarankan diisi</li>
          </ul>
        </li>
        <li>
          Klik tombol <strong>"Simpan Bahan Baku"</strong>
        </li>
        <li>
          Setelah disimpan, Anda akan diarahkan kembali ke daftar bahan baku.
          Bahan baru akan tampil dengan <strong>stok = 0</strong>
        </li>
        <li>
          Klik nama bahan untuk masuk ke halaman detail,
          lalu klik <strong>"Set Stok Awal"</strong> untuk mengisi stok pertama
          beserta harga beli per satuan
        </li>
      </ol>

      <VAlert type="warning" variant="tonal" class="mb-4">
        <strong>⚠️ Perhatikan urutan ini:</strong><br>
        Anda TIDAK bisa langsung isi stok di form tambah bahan.
        Stok awal harus diset terpisah setelah bahan berhasil disimpan.
        Ini memastikan sistem bisa mencatat riwayat stok dari awal dengan benar.
      </VAlert>

      <h6 class="text-subtitle-1 font-weight-bold mb-3">Contoh Pengisian Form:</h6>
      <VAlert type="success" variant="tonal">
        <p class="mb-2">Untuk mendaftarkan <strong>Kopi Arabika Gayo</strong>:</p>
        <ul class="mb-0">
          <li><strong>Nama Bahan:</strong> Kopi Arabika Gayo</li>
          <li><strong>SKU:</strong> KOP-ARA-GAY-001 (atau kosongkan)</li>
          <li><strong>Satuan:</strong> Gram</li>
          <li><strong>Kategori:</strong> Bahan Utama</li>
          <li><strong>Stok Minimum:</strong> 500 (sistem akan peringatkan jika stok &lt; 500g)</li>
          <li><strong>Izinkan Stok Negatif:</strong> Tidak (biarkan default)</li>
        </ul>
      </VAlert>
    `,
  },

  {
    title: '📋 Penjelasan Setiap Kolom di Form',
    icon: 'ri-list-check',
    color: 'primary',
    content: `
      <p class="mb-4">
        Berikut penjelasan lengkap setiap kolom yang perlu Anda isi saat menambah atau mengedit bahan baku.
      </p>

      <VTable class="mb-6">
        <thead>
          <tr>
            <th style="min-width:140px">Nama Kolom</th>
            <th style="width:80px">Wajib?</th>
            <th>Penjelasan</th>
            <th style="min-width:160px">Contoh</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Nama Bahan Baku</strong></td>
            <td><VChip size="x-small" color="error" variant="tonal">Wajib</VChip></td>
            <td>Nama lengkap bahan baku. Gunakan nama yang jelas dan mudah diingat. Boleh gunakan nama lokal/brand.</td>
            <td class="text-caption">Kopi Arabika Gayo, Susu Ultra Full Cream, Gula Aren Cair</td>
          </tr>
          <tr>
            <td><strong>SKU</strong></td>
            <td><VChip size="x-small" color="success" variant="tonal">Opsional</VChip></td>
            <td>
              <strong>Stock Keeping Unit</strong> — kode unik buatan Anda sendiri untuk identifikasi bahan.
              Berguna jika Anda punya banyak bahan dan ingin cari berdasarkan kode.
              Jika dikosongkan, sistem akan buat kode otomatis.
            </td>
            <td class="text-caption">KOP-ARA-001, SUS-ULT-FL-001, GUL-ARN-001</td>
          </tr>
          <tr>
            <td><strong>Satuan</strong></td>
            <td><VChip size="x-small" color="error" variant="tonal">Wajib</VChip></td>
            <td>
              Satuan pengukuran bahan. Pilih dari daftar satuan yang sudah dibuat di menu
              <strong>Satuan</strong>. Jika satuan yang Anda butuhkan belum ada,
              buat dulu di menu Satuan sebelum kembali ke sini.
            </td>
            <td class="text-caption">gram, ml, liter, kg, pcs, lembar, butir</td>
          </tr>
          <tr>
            <td><strong>Kategori</strong></td>
            <td><VChip size="x-small" color="success" variant="tonal">Opsional</VChip></td>
            <td>
              Pengelompokan bahan baku. Membantu pencarian dan laporan.
              Contoh kategori: Bahan Utama, Bahan Pendukung, Kemasan, Alat Habis Pakai.
            </td>
            <td class="text-caption">Bahan Utama, Kemasan, Bahan Pendukung</td>
          </tr>
          <tr>
            <td><strong>Stok Minimum</strong></td>
            <td><VChip size="x-small" color="success" variant="tonal">Opsional</VChip></td>
            <td>
              Batas stok terendah sebelum sistem memberi peringatan.
              Saat stok bahan menyentuh angka ini, akan muncul badge merah
              <strong>"Stok Rendah"</strong> di daftar bahan.
              <br><br>
              Panduan: isi dengan stok yang cukup untuk 3-5 hari operasional normal.
            </td>
            <td class="text-caption">500 (untuk kopi), 2000 (untuk susu ml), 100 (untuk cup pcs)</td>
          </tr>
          <tr>
            <td><strong>Izinkan Stok Negatif</strong></td>
            <td><VChip size="x-small" color="success" variant="tonal">Opsional</VChip></td>
            <td>
              Jika diaktifkan, sistem akan tetap memproses transaksi meskipun stok bahan = 0.
              Ini bisa terjadi jika pencatatan stok tidak real-time.
              <br><br>
              <strong>Rekomendasi: biarkan nonaktif</strong> agar Anda tahu persis
              kapan bahan benar-benar habis.
            </td>
            <td class="text-caption">Nonaktif (default)</td>
          </tr>
          <tr>
            <td><strong>Status Aktif</strong></td>
            <td><VChip size="x-small" color="success" variant="tonal">Opsional</VChip></td>
            <td>
              Bahan yang nonaktif tidak akan bisa digunakan di resep produk baru,
              tapi riwayat penggunaannya tetap tersimpan.
              Gunakan ini daripada menghapus bahan yang sudah pernah dipakai.
            </td>
            <td class="text-caption">Aktif (default)</td>
          </tr>
        </tbody>
      </VTable>
    `,
  },

  {
    title: '📊 Cara Melihat Daftar Bahan Baku',
    icon: 'ri-list-unordered',
    color: 'secondary',
    content: `
      <p class="mb-4">
        Halaman daftar bahan baku menampilkan semua bahan yang sudah Anda daftarkan
        beserta informasi stok terkini.
      </p>

      <h6 class="text-subtitle-1 font-weight-bold mb-3">Kolom yang Ditampilkan:</h6>
      <VTable class="mb-5">
        <thead>
          <tr>
            <th>Kolom</th>
            <th>Penjelasan</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Nama Bahan</strong></td>
            <td>
              Nama bahan baku. Jika stok sudah di bawah minimum, akan muncul badge
              oranye <strong>"Stok Rendah"</strong> di bawah nama.
            </td>
          </tr>
          <tr>
            <td><strong>SKU</strong></td>
            <td>Kode identifikasi bahan (jika diisi saat tambah bahan)</td>
          </tr>
          <tr>
            <td><strong>Kategori</strong></td>
            <td>Kelompok bahan (jika dipilih saat tambah bahan)</td>
          </tr>
          <tr>
            <td><strong>Stok Saat Ini</strong></td>
            <td>
              Jumlah stok yang tersedia sekarang dalam satuan bahan tersebut.
              Angka ini berkurang otomatis setiap ada penjualan produk.
            </td>
          </tr>
          <tr>
            <td><strong>Min Stok</strong></td>
            <td>Batas minimum stok sebelum peringatan muncul</td>
          </tr>
          <tr>
            <td><strong>HPP Rata-rata</strong></td>
            <td>
              <strong>Harga Pokok rata-rata per satuan</strong> — dihitung otomatis
              menggunakan metode WAC (Weighted Average Cost / Rata-rata Tertimbang).
              Angka ini berubah setiap ada pembelian bahan dengan harga berbeda.
              <br><br>
              <em>Contoh: Anda beli kopi 1kg seharga Rp 150.000, lalu beli lagi 500g
              seharga Rp 80.000. HPP rata-rata = (150.000 + 80.000) / (1000 + 500)g
              = Rp 153/gram.</em>
            </td>
          </tr>
          <tr>
            <td><strong>Nilai Stok</strong></td>
            <td>
              Total nilai bahan yang masih ada di gudang.
              Dihitung dari: <strong>Stok Saat Ini × HPP Rata-rata</strong>.
              Berguna untuk laporan aset inventori.
            </td>
          </tr>
          <tr>
            <td><strong>Status</strong></td>
            <td>Aktif atau Nonaktif</td>
          </tr>
        </tbody>
      </VTable>

      <h6 class="text-subtitle-1 font-weight-bold mb-3">Fitur Pencarian & Filter:</h6>
      <ul class="mb-0">
        <li class="mb-2">
          <strong>Kotak Pencarian</strong> — ketik nama atau SKU bahan untuk mencari bahan tertentu
        </li>
        <li class="mb-2">
          <strong>Filter Kategori</strong> — tampilkan hanya bahan dalam kategori tertentu
        </li>
        <li class="mb-2">
          <strong>Filter Status</strong> — tampilkan semua / hanya aktif / hanya nonaktif
        </li>
        <li class="mb-2">
          <strong>Tombol "Stok Rendah"</strong> — klik untuk langsung melihat
          semua bahan yang perlu segera dibeli ulang
        </li>
      </ul>
    `,
  },

  {
    title: '🔢 Cara Set Stok Awal (Smart HPP Calculator)',
    icon: 'ri-stock-line',
    color: 'warning',
    content: `
      <p class="mb-4">
        Setelah bahan baku baru disimpan, stok awalnya masih <strong>0</strong>.
        Anda perlu melakukan "Set Stok Awal" untuk mengisi jumlah stok yang
        sebenarnya ada di gudang saat ini. Anda bisa mengisi Stok Awal langsung
        di form <strong>Tambah Bahan Baku</strong> (kartu "Stok Awal (Opsional)" di
        bagian bawah), atau belakangan dari <strong>halaman detail bahan</strong>.
      </p>

      <VAlert type="info" variant="tonal" class="mb-4">
        <strong>Kenapa ada langkah Set Stok Awal yang terpisah?</strong><br>
        Karena sistem perlu mencatat <strong>harga beli</strong> saat stok pertama masuk.
        Harga ini dipakai untuk menghitung HPP produk Anda. Tanpa harga beli,
        sistem tidak bisa menghitung berapa modal yang Anda keluarkan per cup kopi.
      </VAlert>

      <h6 class="text-subtitle-1 font-weight-bold mb-2">✨ Sekarang Ada Smart HPP Calculator</h6>
      <p class="mb-3">
        Anda tidak perlu lagi menghitung sendiri harga per gram/ml. Cukup ketik
        apa yang benar-benar Anda beli — persis seperti di kuitansi/nota supplier —
        dan sistem otomatis menghitung <strong>HPP per satuan dasar</strong> bahan itu.
      </p>

      <VTable class="mb-4">
        <thead>
          <tr>
            <th style="min-width:160px">Kolom di Form</th>
            <th>Penjelasan</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Jumlah Pembelian</strong></td>
            <td>Berapa banyak yang Anda beli, dalam <em>Satuan Pembelian</em> yang dipilih di sebelahnya. Contoh: <code>1</code> (kalau beli 1 Kg).</td>
          </tr>
          <tr>
            <td><strong>Satuan (Satuan Pembelian)</strong></td>
            <td>
              Satuan yang tertulis di nota belanja Anda — boleh satuan dasar bahan
              (mis. Gram) <em>atau</em> satuan turunannya yang lebih besar (mis. Kilogram),
              <strong>kalau sudah dihubungkan</strong> di menu <strong>Satuan</strong>
              (lihat panduan Satuan untuk cara menghubungkan Kilogram → Gram).
              Kalau belum dihubungkan, hanya satuan dasar bahan yang bisa dipilih.
            </td>
          </tr>
          <tr>
            <td><strong>Total Harga Pembelian</strong></td>
            <td>Total uang yang Anda bayar untuk pembelian ini (Rupiah) — bukan harga per satuan. Contoh: <code>18000</code> untuk 1 Kg kopi seharga Rp18.000.</td>
          </tr>
          <tr>
            <td><strong>HPP per &lt;satuan dasar&gt; (Otomatis)</strong></td>
            <td>
              <strong>Read-only</strong>, dihitung otomatis realtime = Total Harga ÷ Jumlah,
              lalu dikonversi ke satuan dasar bahan. Ini yang tersimpan sebagai HPP
              awal bahan Anda — bukan angka yang Anda ketik sendiri.
            </td>
          </tr>
        </tbody>
      </VTable>

      <h6 class="text-subtitle-1 font-weight-bold mb-3">Contoh Pengisian (beli dalam Kilogram, stok dalam Gram):</h6>
      <VAlert type="success" variant="tonal" class="mb-4">
        <p class="mb-2">
          Bahan <strong>Kopi Arabika Gayo</strong> punya satuan dasar <strong>Gram</strong>,
          tapi Anda belinya di pasar dalam <strong>Kilogram</strong>:
        </p>
        <ul class="mb-0">
          <li><strong>Jumlah Pembelian:</strong> 1</li>
          <li><strong>Satuan:</strong> Kilogram</li>
          <li><strong>Total Harga Pembelian:</strong> Rp 18.000</li>
        </ul>
        <p class="mt-2 mb-0">
          Sistem otomatis menghitung: 1 Kg = 1.000 gram → stok bertambah
          <strong>1.000 gram</strong>, dan <strong>HPP per Gram = Rp 18.000 ÷ 1.000 = Rp 18/gram</strong>.
          Anda tidak perlu membagi angkanya sendiri.
        </p>
      </VAlert>

      <VAlert type="error" variant="tonal">
        <strong>⚠️ PERHATIAN:</strong> Set Stok Awal hanya bisa dilakukan
        <strong>SATU KALI</strong> saat stok = 0. Setelah stok terisi,
        gunakan fitur <strong>"Sesuaikan Stok"</strong> untuk koreksi,
        atau <strong>"Purchase Order &amp; Receiving"</strong> untuk penambahan
        dari pembelian berikutnya — keduanya juga sudah pakai Smart HPP Calculator
        yang sama.
      </VAlert>
    `,
  },

  {
    title: '⚖️ Cara Menyesuaikan Stok (Stock Adjustment)',
    icon: 'ri-equalizer-line',
    color: 'info',
    content: `
      <p class="mb-4">
        Penyesuaian stok dilakukan saat stok di sistem <strong>tidak sesuai</strong>
        dengan stok fisik yang ada di gudang. Ini bisa terjadi karena:
      </p>
      <ul class="mb-4">
        <li>Bahan tercecer atau rusak saat penyimpanan</li>
        <li>Ada penggunaan bahan yang tidak tercatat di sistem</li>
        <li>Kesalahan pencatatan sebelumnya</li>
        <li>Hasil penghitungan fisik (stock opname) berbeda dengan sistem</li>
      </ul>

      <h6 class="text-subtitle-1 font-weight-bold mb-3">Langkah-langkah:</h6>
      <ol class="mb-4" style="line-height: 2.2">
        <li>Buka halaman detail bahan baku (klik ikon 👁 di daftar bahan)</li>
        <li>Klik tombol <strong>"Sesuaikan Stok"</strong></li>
        <li>Isi form:
          <ul class="mt-2">
            <li><strong>Stok Baru</strong> — masukkan jumlah stok yang <em>sebenarnya ada</em> setelah dihitung fisik</li>
            <li><strong>Alasan</strong> — wajib diisi, jelaskan mengapa ada penyesuaian</li>
          </ul>
        </li>
        <li>Sistem akan menampilkan <strong>selisih</strong> (+ atau -) sebelum Anda konfirmasi</li>
        <li>Klik <strong>"Simpan Penyesuaian"</strong></li>
      </ol>

      <h6 class="text-subtitle-1 font-weight-bold mb-3">Contoh Kasus:</h6>
      <VAlert type="info" variant="tonal" class="mb-4">
        <p class="mb-2">Sistem menunjukkan stok kopi = <strong>2.500 gram</strong>,
        tapi setelah dihitung fisik ternyata hanya ada <strong>2.200 gram</strong>.</p>
        <ul class="mb-2">
          <li><strong>Stok Baru:</strong> 2200</li>
          <li><strong>Alasan:</strong> Koreksi setelah hitung fisik bulanan, selisih 300g kemungkinan tercecer</li>
        </ul>
        <p class="mb-0">Sistem akan mencatat selisih: <strong>-300 gram</strong> (pengurangan)</p>
      </VAlert>

      <VAlert type="warning" variant="tonal">
        <strong>💡 Tips:</strong> Selalu lakukan penghitungan fisik bahan
        sebelum menyesuaikan stok. Jangan hanya menebak. Penyesuaian stok
        akan dicatat di riwayat dan tidak bisa dihapus.
      </VAlert>
    `,
  },

  {
    title: '📜 Riwayat Pergerakan Stok',
    icon: 'ri-history-line',
    color: 'secondary',
    content: `
      <p class="mb-4">
        Setiap perubahan stok — baik masuk maupun keluar — dicatat otomatis oleh sistem.
        Anda bisa melihat riwayat lengkap ini di halaman detail bahan baku.
      </p>

      <h6 class="text-subtitle-1 font-weight-bold mb-3">Jenis Pergerakan Stok:</h6>
      <VTable class="mb-5">
        <thead>
          <tr>
            <th>Jenis</th>
            <th>Warna</th>
            <th>Artinya</th>
            <th>Contoh</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><VChip size="small" color="primary" variant="tonal">Stok Awal</VChip></td>
            <td>Biru</td>
            <td>Pengisian stok pertama kali</td>
            <td>Set stok awal 3.000g kopi</td>
          </tr>
          <tr>
            <td><VChip size="small" color="success" variant="tonal">Pembelian</VChip></td>
            <td>Hijau</td>
            <td>Stok bertambah dari pembelian ke supplier</td>
            <td>Terima PO dari Supplier ABC: +2.000g</td>
          </tr>
          <tr>
            <td><VChip size="small" color="warning" variant="tonal">Penjualan</VChip></td>
            <td>Oranye</td>
            <td>Stok berkurang karena produk terjual</td>
            <td>Jual 5 cup Latte: -90g kopi</td>
          </tr>
          <tr>
            <td><VChip size="small" color="info" variant="tonal">Penyesuaian</VChip></td>
            <td>Cyan</td>
            <td>Koreksi manual dari admin</td>
            <td>Koreksi hitung fisik: -300g</td>
          </tr>
          <tr>
            <td><VChip size="small" color="error" variant="tonal">Pembuangan</VChip></td>
            <td>Merah</td>
            <td>Bahan dibuang karena rusak/expired</td>
            <td>Susu expired: -500ml</td>
          </tr>
          <tr>
            <td><VChip size="small" color="teal" variant="tonal">Retur</VChip></td>
            <td>Teal</td>
            <td>Bahan dikembalikan ke supplier</td>
            <td>Retur kopi cacat: -200g</td>
          </tr>
        </tbody>
      </VTable>

      <h6 class="text-subtitle-1 font-weight-bold mb-3">Cara Menggunakan Filter Riwayat:</h6>
      <ul class="mb-0">
        <li class="mb-2">
          <strong>Filter Tipe</strong> — pilih jenis pergerakan tertentu
          (misal: lihat semua Penyesuaian saja)
        </li>
        <li class="mb-2">
          <strong>Filter Tanggal</strong> — pilih rentang tanggal untuk laporan periode tertentu
          (misal: pergerakan bulan ini saja)
        </li>
        <li class="mb-2">
          <strong>Kolom Qty</strong> — angka hijau (+) berarti stok bertambah,
          angka merah (-) berarti stok berkurang
        </li>
      </ul>
    `,
  },

  {
    title: '⚠️ Peringatan & Batasan Penting',
    icon: 'ri-alert-line',
    color: 'error',
    content: `
      <VAlert type="error" variant="tonal" class="mb-4">
        <strong>Tidak bisa menghapus bahan yang sudah pernah dipakai!</strong><br>
        Bahan baku yang sudah memiliki riwayat stok (pernah diisi, dibeli, atau dipakai
        dalam transaksi) tidak bisa dihapus dari sistem.
        <br><br>
        <strong>Solusinya:</strong> Matikan status aktif bahan tersebut agar tidak
        muncul di pilihan resep produk baru, tapi riwayatnya tetap tersimpan.
      </VAlert>

      <VAlert type="warning" variant="tonal" class="mb-4">
        <strong>Satuan tidak bisa diganti setelah bahan dipakai!</strong><br>
        Jika Anda sudah memilih satuan "gram" dan bahan sudah memiliki stok,
        Anda tidak bisa ganti ke "kg". Ini untuk menjaga konsistensi data.
        <br><br>
        <strong>Solusinya:</strong> Nonaktifkan bahan yang salah satuan, lalu buat
        bahan baru dengan satuan yang benar.
      </VAlert>

      <VAlert type="info" variant="tonal" class="mb-4">
        <strong>Perubahan di form Edit tidak mengubah stok!</strong><br>
        Form edit bahan baku hanya mengubah informasi bahan (nama, kategori, satuan, dll).
        Untuk mengubah jumlah stok, gunakan fitur <strong>"Sesuaikan Stok"</strong>
        di halaman detail.
      </VAlert>

      <VAlert type="warning" variant="tonal">
        <strong>HPP rata-rata berubah setiap ada pembelian!</strong><br>
        Setiap kali Anda menerima bahan dari supplier dengan harga yang berbeda,
        HPP rata-rata akan dihitung ulang menggunakan metode WAC.
        Harga lama tidak berubah, tapi rata-ratanya akan menyesuaikan.
      </VAlert>
    `,
  },

  {
    title: '💡 Tips & Best Practices',
    icon: 'ri-lightbulb-line',
    color: 'warning',
    content: `
      <VRow>
        <VCol cols="12" md="6">
          <VCard variant="outlined" class="mb-4 h-100">
            <VCardText>
              <div class="d-flex align-center mb-3">
                <VIcon icon="ri-price-tag-3-line" color="warning" class="me-2" size="24" />
                <strong>Penamaan yang Konsisten</strong>
              </div>
              <p class="text-body-2 mb-2">
                Gunakan format nama yang konsisten untuk memudahkan pencarian.
              </p>
              <p class="text-body-2 mb-0">
                <strong>Contoh format:</strong><br>
                ✅ Kopi Arabika — Gayo<br>
                ✅ Kopi Robusta — Lampung<br>
                ✅ Susu — UHT Full Cream<br>
                ✅ Susu — Segar Pasteurisasi<br>
                ❌ kopi1, kopinya, susu uht
              </p>
            </VCardText>
          </VCard>
        </VCol>

        <VCol cols="12" md="6">
          <VCard variant="outlined" class="mb-4 h-100">
            <VCardText>
              <div class="d-flex align-center mb-3">
                <VIcon icon="ri-alarm-warning-line" color="error" class="me-2" size="24" />
                <strong>Stok Minimum yang Tepat</strong>
              </div>
              <p class="text-body-2 mb-2">
                Isi stok minimum dengan jumlah yang cukup untuk <strong>3-5 hari operasional</strong>.
              </p>
              <p class="text-body-2 mb-0">
                <strong>Contoh untuk coffee shop:</strong><br>
                • Kopi: minimal 500g (pemakaian harian ~100-150g)<br>
                • Susu: minimal 3000ml (pemakaian harian ~500ml)<br>
                • Cup: minimal 100 pcs (pemakaian harian ~30-50 pcs)
              </p>
            </VCardText>
          </VCard>
        </VCol>

        <VCol cols="12" md="6">
          <VCard variant="outlined" class="mb-4 h-100">
            <VCardText>
              <div class="d-flex align-center mb-3">
                <VIcon icon="ri-folder-3-line" color="info" class="me-2" size="24" />
                <strong>Gunakan Kategori</strong>
              </div>
              <p class="text-body-2 mb-2">
                Kelompokan bahan dengan kategori untuk memudahkan pengelolaan.
              </p>
              <p class="text-body-2 mb-0">
                <strong>Contoh kategori untuk coffee shop:</strong><br>
                • Bahan Utama (kopi, susu, sirup)<br>
                • Kemasan (cup, lid, sedotan, paper bag)<br>
                • Bahan Pendukung (gula, garam, es batu)<br>
                • Alat Habis Pakai (tissue, sarung tangan)
              </p>
            </VCardText>
          </VCard>
        </VCol>

        <VCol cols="12" md="6">
          <VCard variant="outlined" class="mb-4 h-100">
            <VCardText>
              <div class="d-flex align-center mb-3">
                <VIcon icon="ri-scales-3-line" color="success" class="me-2" size="24" />
                <strong>Satuan yang Tepat</strong>
              </div>
              <p class="text-body-2 mb-2">
                Pilih satuan terkecil yang paling sering Anda gunakan dalam resep.
              </p>
              <p class="text-body-2 mb-0">
                <strong>Contoh:</strong><br>
                ✅ Kopi → gram (bukan kg, karena resep pakai gram)<br>
                ✅ Susu → ml (bukan liter)<br>
                ✅ Gula cair → ml<br>
                ✅ Cup → pcs<br>
                ❌ Kopi → sendok teh (tidak presisi)
              </p>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>

      <VAlert type="success" variant="tonal">
        <strong>🏆 Rutinitas yang Disarankan:</strong><br>
        <ul class="mt-2 mb-0">
          <li><strong>Harian:</strong> Cek daftar "Stok Rendah" setiap pagi sebelum buka</li>
          <li><strong>Mingguan:</strong> Hitung fisik bahan yang paling cepat habis</li>
          <li><strong>Bulanan:</strong> Stock opname lengkap semua bahan</li>
        </ul>
      </VAlert>
    `,
  },

  {
    title: '❓ Pertanyaan yang Sering Diajukan (FAQ)',
    icon: 'ri-question-answer-line',
    color: 'primary',
    content: `
      <VExpansionPanels variant="accordion">
        <VExpansionPanel>
          <VExpansionPanelTitle>
            Stok di sistem berkurang otomatis, tapi saya tidak ingat melakukan apa-apa?
          </VExpansionPanelTitle>
          <VExpansionPanelText>
            <p>
              Stok berkurang otomatis setiap kali ada <strong>transaksi penjualan</strong>
              yang berhasil diproses oleh kasir. Jika ada produk yang terjual dan produk tersebut
              memiliki resep (BOM), bahan-bahannya akan langsung dikurangi.
            </p>
            <p class="mb-0">
              Cek riwayat pergerakan stok di halaman detail bahan — cari jenis
              <strong>"Penjualan"</strong> (warna oranye) untuk melihat kapan dan
              berapa banyak yang terpakai.
            </p>
          </VExpansionPanelText>
        </VExpansionPanel>

        <VExpansionPanel>
          <VExpansionPanelTitle>
            Saya beli kopi dengan harga berbeda dari pembelian sebelumnya. HPP akan berubah?
          </VExpansionPanelTitle>
          <VExpansionPanelText>
            <p>
              Ya, HPP rata-rata akan berubah menggunakan <strong>metode WAC
              (Weighted Average Cost)</strong> atau rata-rata tertimbang.
            </p>
            <p class="mb-0">
              <strong>Contoh:</strong><br>
              • Stok lama: 1.000g @ Rp 150/g = Rp 150.000<br>
              • Beli baru: 500g @ Rp 170/g = Rp 85.000<br>
              • Total: 1.500g senilai Rp 235.000<br>
              • HPP rata-rata baru: Rp 235.000 / 1.500 = <strong>Rp 156,67/g</strong>
            </p>
          </VExpansionPanelText>
        </VExpansionPanel>

        <VExpansionPanel>
          <VExpansionPanelTitle>
            Apakah saya bisa import daftar bahan baku dari Excel?
          </VExpansionPanelTitle>
          <VExpansionPanelText>
            <p>
              Fitur import Excel saat ini belum tersedia. Bahan baku perlu didaftarkan
              satu per satu melalui form tambah bahan baku.
            </p>
            <p class="mb-0">
              Jika Anda memiliki banyak bahan (50+), disarankan mendaftarkan
              dulu semua bahan tanpa stok, lalu isi stok awal secara bertahap.
              Biasanya proses ini bisa diselesaikan dalam 1-2 jam.
            </p>
          </VExpansionPanelText>
        </VExpansionPanel>

        <VExpansionPanel>
          <VExpansionPanelTitle>
            Bahan saya sudah tidak dipakai lagi. Apakah bisa dihapus?
          </VExpansionPanelTitle>
          <VExpansionPanelText>
            <p>
              Jika bahan sudah pernah memiliki stok atau digunakan dalam transaksi,
              bahan <strong>tidak bisa dihapus</strong> karena riwayatnya perlu
              tetap tersimpan untuk keperluan laporan dan audit.
            </p>
            <p class="mb-0">
              <strong>Solusi terbaik:</strong> Masuk ke edit bahan baku,
              lalu matikan <strong>Status Aktif</strong>. Bahan nonaktif
              tidak akan muncul saat membuat resep produk baru,
              tapi riwayat lamanya tetap ada.
            </p>
          </VExpansionPanelText>
        </VExpansionPanel>

        <VExpansionPanel>
          <VExpansionPanelTitle>
            Mengapa tombol "Set Stok Awal" tidak muncul?
          </VExpansionPanelTitle>
          <VExpansionPanelText>
            <p>
              Tombol <strong>"Set Stok Awal"</strong> hanya muncul saat stok bahan = 0.
              Jika stok sudah pernah terisi (meskipun sekarang mungkin sudah 0 karena
              habis terpakai), tombol ini tidak akan muncul lagi.
            </p>
            <p class="mb-0">
              Untuk mengubah stok yang sudah pernah terisi, gunakan tombol
              <strong>"Sesuaikan Stok"</strong> — tombol ini akan muncul
              menggantikan "Set Stok Awal" setelah stok pertama diisi.
            </p>
          </VExpansionPanelText>
        </VExpansionPanel>

        <VExpansionPanel>
          <VExpansionPanelTitle>
            Apa bedanya Penyesuaian Stok dengan Stock Opname?
          </VExpansionPanelTitle>
          <VExpansionPanelText>
            <p>
              <strong>Penyesuaian Stok</strong> dilakukan untuk <em>satu bahan spesifik</em>
              kapan saja, misalnya saat menemukan selisih pada bahan tertentu.
            </p>
            <p class="mb-0">
              <strong>Stock Opname</strong> adalah proses penghitungan fisik
              <em>semua bahan sekaligus</em> pada waktu tertentu (misal: akhir bulan).
              Hasilnya bisa langsung diinput dan sistem akan menyesuaikan stok
              semua bahan yang berbeda. Fitur ini ada di menu <strong>Inventory > Stock Opname</strong>.
            </p>
          </VExpansionPanelText>
        </VExpansionPanel>
      </VExpansionPanels>
    `,
  },
]
</script>

<template>
  <div>
    <!-- Back Button -->
    <VRow>
      <VCol cols="12">
        <RouterLink
          to="/userguide"
          class="text-decoration-none"
        >
          <VBtn
            variant="text"
            prepend-icon="ri-arrow-left-line"
            class="mb-2 px-0"
          >
            Kembali ke Produk & Inventory
          </VBtn>
        </RouterLink>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow class="mb-2">
      <VCol cols="12">
        <div class="d-flex align-center flex-wrap gap-4 mb-4">
          <VAvatar
            color="info"
            variant="tonal"
            size="72"
            rounded="lg"
          >
            <VIcon
              icon="ri-archive-line"
              size="40"
            />
          </VAvatar>
          <div>
            <div class="text-caption text-medium-emphasis mb-1">
              Modul Produk & Inventory
            </div>
            <h4 class="text-h4 font-weight-bold mb-1">
              Bahan Baku
            </h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Kelola semua bahan yang Anda gunakan untuk membuat produk
            </p>
          </div>
        </div>

        <!-- Quick Info Bar -->
        <VRow class="mb-2">
          <VCol
            cols="6"
            sm="3"
          >
            <VCard
              variant="tonal"
              color="success"
            >
              <VCardText class="text-center pa-3">
                <VIcon
                  icon="ri-add-circle-line"
                  size="28"
                  class="mb-1"
                />
                <div class="text-caption">Tambah Bahan</div>
              </VCardText>
            </VCard>
          </VCol>
          <VCol
            cols="6"
            sm="3"
          >
            <VCard
              variant="tonal"
              color="warning"
            >
              <VCardText class="text-center pa-3">
                <VIcon
                  icon="ri-stock-line"
                  size="28"
                  class="mb-1"
                />
                <div class="text-caption">Set Stok Awal</div>
              </VCardText>
            </VCard>
          </VCol>
          <VCol
            cols="6"
            sm="3"
          >
            <VCard
              variant="tonal"
              color="info"
            >
              <VCardText class="text-center pa-3">
                <VIcon
                  icon="ri-equalizer-line"
                  size="28"
                  class="mb-1"
                />
                <div class="text-caption">Sesuaikan Stok</div>
              </VCardText>
            </VCard>
          </VCol>
          <VCol
            cols="6"
            sm="3"
          >
            <VCard
              variant="tonal"
              color="secondary"
            >
              <VCardText class="text-center pa-3">
                <VIcon
                  icon="ri-history-line"
                  size="28"
                  class="mb-1"
                />
                <div class="text-caption">Riwayat Stok</div>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>
      </VCol>
    </VRow>

    <!-- Sections -->
    <VRow>
      <VCol cols="12">
        <VExpansionPanels
          variant="accordion"
          multiple
          :model-value="[0, 1]"
        >
          <VExpansionPanel
            v-for="(section, index) in sections"
            :key="index"
          >
            <VExpansionPanelTitle class="py-4">
              <div class="d-flex align-center">
                <VIcon
                  :icon="section.icon"
                  :color="section.color"
                  size="22"
                  class="me-3 flex-shrink-0"
                />
                <span class="font-weight-medium">{{ section.title }}</span>
              </div>
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <div
                class="text-body-2 pa-2"
                style="line-height: 1.8"
                v-html="section.content"
              />
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related Pages -->
    <VRow class="mt-8">
      <VCol cols="12">
        <h5 class="text-h5 mb-4">
          🔗 Halaman Terkait
        </h5>
        <VRow>
          <VCol
            cols="12"
            sm="6"
            md="4"
          >
            <RouterLink
              to="/userguide/produk-inventory/satuan"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3">
                  <VAvatar
                    color="primary"
                    variant="tonal"
                    size="44"
                  >
                    <VIcon icon="ri-ruler-2-line" />
                  </VAvatar>
                  <div>
                    <div class="font-weight-medium">Satuan</div>
                    <div class="text-caption text-medium-emphasis">
                      Buat satuan pengukuran (gram, ml, pcs)
                    </div>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>

          <VCol
            cols="12"
            sm="6"
            md="4"
          >
            <RouterLink
              to="/userguide/produk-inventory/kategori"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3">
                  <VAvatar
                    color="success"
                    variant="tonal"
                    size="44"
                  >
                    <VIcon icon="ri-folder-3-line" />
                  </VAvatar>
                  <div>
                    <div class="font-weight-medium">Kategori</div>
                    <div class="text-caption text-medium-emphasis">
                      Kelompokan bahan dengan kategori
                    </div>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>

          <VCol
            cols="12"
            sm="6"
            md="4"
          >
            <RouterLink
              to="/userguide/produk-inventory/stock-opname"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3">
                  <VAvatar
                    color="warning"
                    variant="tonal"
                    size="44"
                  >
                    <VIcon icon="ri-clipboard-line" />
                  </VAvatar>
                  <div>
                    <div class="font-weight-medium">Stock Opname</div>
                    <div class="text-caption text-medium-emphasis">
                      Hitung fisik semua bahan sekaligus
                    </div>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>

          <VCol
            cols="12"
            sm="6"
            md="4"
          >
            <RouterLink
              to="/userguide/produk-inventory/produk"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3">
                  <VAvatar
                    color="info"
                    variant="tonal"
                    size="44"
                  >
                    <VIcon icon="ri-cup-line" />
                  </VAvatar>
                  <div>
                    <div class="font-weight-medium">Produk & Resep</div>
                    <div class="text-caption text-medium-emphasis">
                      Hubungkan bahan baku ke produk
                    </div>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>

          <VCol
            cols="12"
            sm="6"
            md="4"
          >
            <RouterLink
              to="/userguide/pembelian/purchase-order"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3">
                  <VAvatar
                    color="error"
                    variant="tonal"
                    size="44"
                  >
                    <VIcon icon="ri-truck-line" />
                  </VAvatar>
                  <div>
                    <div class="font-weight-medium">Purchase Order</div>
                    <div class="text-caption text-medium-emphasis">
                      Beli bahan dari supplier untuk isi stok
                    </div>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>

          <VCol
            cols="12"
            sm="6"
            md="4"
          >
            <RouterLink
              to="/userguide/laporan/inventory"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3">
                  <VAvatar
                    color="secondary"
                    variant="tonal"
                    size="44"
                  >
                    <VIcon icon="ri-bar-chart-line" />
                  </VAvatar>
                  <div>
                    <div class="font-weight-medium">Laporan Inventory</div>
                    <div class="text-caption text-medium-emphasis">
                      Analisis penggunaan dan nilai stok
                    </div>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
